import type { ReactNode } from 'react'
import { Text, View } from 'react-native'
import { Image } from 'expo-image'
import { QuerySuspenseBoundary } from '@/src/components/ui/QuerySuspenseBoundary'
import {
  fetchDashboardSubjects,
  fetchDashboardWeeks,
} from '@/src/dal/contentful-dashboard-dal'
import {
  buildProgressCountMap,
  buildWeeklyExercises,
  resolveDashboardWeeks,
} from '@/src/queries/dashboard-queries'
import { progressSnapshotQueryOptions } from '@/src/queries/progress-queries'
import { queryKeys } from '@/src/queries/query-keys'
import { colors } from '@/src/theme'
import { body1, body3, label1 } from '@/src/theme/typography'
import { DashboardExerciseCard } from '@/src/types/dashboard'
import { captureException } from '@/src/utils/sentry'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { SectionErrorState } from '../../../components/ui/SectionErrorState'
import { getExerciseSubtitle } from '../utils/dashboard-screen-helpers'
import {
  WeeklyExercisesSection,
  WeeklyExercisesSectionSkeleton,
} from './DashboardExercisesSection'

function getExerciseIcon(exercise: DashboardExerciseCard) {
  if (exercise.iconUrl == null) {
    return undefined
  }

  return (
    <Image
      source={{ uri: exercise.iconUrl }}
      style={{ width: 32, height: 32 }}
      contentFit="contain"
    />
  )
}

type ViewedWeekWeeklyExercisesSectionProps = {
  currentProgramWeek: number
  handleExercisePress: (exercise: DashboardExerciseCard) => void
  hasPurchased: boolean
  language: string
  selectedProgramWeek: number
  weekPickerSlot?: ReactNode
}

function WeeklyExercisesSectionHeader({
  weekPickerSlot,
}: Readonly<{ weekPickerSlot?: ReactNode }>) {
  const { t } = useTranslation(['dashboard', 'common'])

  return (
    <View className="mb-3.5 flex-row items-center justify-between">
      <Text className="text-ink" style={body1}>
        {t('dashboard:weekly_exercise_section')}
      </Text>
      {weekPickerSlot}
    </View>
  )
}

function WeeklyExercisesSectionError({
  reset,
  weekPickerSlot,
}: Readonly<{
  reset: () => void
  weekPickerSlot?: ReactNode
}>) {
  const { t } = useTranslation(['dashboard', 'common'])

  return (
    <View className="mb-8">
      <WeeklyExercisesSectionHeader weekPickerSlot={weekPickerSlot} />
      <SectionErrorState
        testID="weekly-exercises-error"
        title={t('dashboard:weekly_exercises_error_title')}
        message={t('dashboard:weekly_exercises_error_message')}
        onRetry={reset}
      />
    </View>
  )
}

function WeeklyExercisesSectionEmpty({
  weekPickerSlot,
}: Readonly<{ weekPickerSlot?: ReactNode }>) {
  const { t } = useTranslation(['dashboard', 'common'])

  return (
    <View className="mb-8">
      <WeeklyExercisesSectionHeader weekPickerSlot={weekPickerSlot} />
      <View
        testID="weekly-exercises-empty"
        className="rounded-lg border border-gray-50 bg-background-secondary px-4 py-5"
        style={{ boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)' }}
      >
        <Text style={{ ...label1, color: colors.textHeader }}>
          {t('dashboard:weekly_exercises_empty_title')}
        </Text>
        <Text className="mt-1" style={{ ...body3, color: colors.textBody }}>
          {t('dashboard:weekly_exercises_empty_message')}
        </Text>
      </View>
    </View>
  )
}

function ReadyViewedWeekWeeklyExercisesSection({
  currentProgramWeek,
  handleExercisePress,
  hasPurchased,
  language,
  selectedProgramWeek,
  weekPickerSlot,
}: ViewedWeekWeeklyExercisesSectionProps) {
  const subjectsQuery = useSuspenseQuery(
    queryOptions({
      queryKey: queryKeys.dashboard.subjects.byLocale(language),
      queryFn: () => fetchDashboardSubjects(language),
    }),
  )
  const weeksQuery = useSuspenseQuery(
    queryOptions({
      queryKey: queryKeys.dashboard.weeks.byLocale(language),
      queryFn: () => fetchDashboardWeeks(language),
    }),
  )
  const resolvedWeek = resolveDashboardWeeks({
    currentProgramWeek,
    selectedWeek: selectedProgramWeek,
    weeks: weeksQuery.data,
  })

  const progressSnapshotQuery = useSuspenseQuery(
    progressSnapshotQueryOptions(resolvedWeek.yearWeek),
  )

  const subjects = subjectsQuery.data
  const progressSnapshot = progressSnapshotQuery.data

  const progressCountMap = buildProgressCountMap(progressSnapshot.progressItems)

  const weeklyExercises = buildWeeklyExercises({
    subjects,
    activeWeek: resolvedWeek.selectedProgramWeek,
    progressCountMap,
  })

  const data = {
    activeWeek: resolvedWeek.selectedProgramWeek,
    contentWeek: resolvedWeek.selectedProgramWeek,
    weeklyExercises,
  }

  const weeklyExercisesWithDisplay = data.weeklyExercises.map((exercise) => ({
    ...exercise,
    icon: getExerciseIcon(exercise),
    subtitle: getExerciseSubtitle(exercise),
  }))
  if (weeklyExercisesWithDisplay.length === 0) {
    return <WeeklyExercisesSectionEmpty weekPickerSlot={weekPickerSlot} />
  }

  return (
    <WeeklyExercisesSection
      handleExercisePress={handleExercisePress}
      hasPurchased={hasPurchased}
      weeklyExercises={weeklyExercisesWithDisplay}
      weekPickerSlot={weekPickerSlot}
    />
  )
}

export function ViewedWeekWeeklyExercisesSection(
  props: ViewedWeekWeeklyExercisesSectionProps,
) {
  return (
    <QuerySuspenseBoundary
      loadingFallback={
        <WeeklyExercisesSectionSkeleton weekPickerSlot={props.weekPickerSlot} />
      }
      errorFallback={({ reset }) => (
        <WeeklyExercisesSectionError
          reset={reset}
          weekPickerSlot={props.weekPickerSlot}
        />
      )}
      onError={(error) => {
        captureException(error, {
          feature: 'dashboard',
          tags: { component: 'ViewedWeekWeeklyExercisesSection' },
        })
      }}
    >
      <ReadyViewedWeekWeeklyExercisesSection {...props} />
    </QuerySuspenseBoundary>
  )
}
